import React, { useState, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';

interface BackupEntry {
  id: number;
  database: string;
  snapshot: string;
  type: 'Auto' | 'Manual';
  size: string;
  createdAt: string;
  status: 'Success' | 'Failed' | 'Partial';
}

const BACKUP_HISTORY: BackupEntry[] = [
  { id: 1, database: 'hci_production', snapshot: 'Full snapshot', type: 'Auto',   size: '1.2 GB', createdAt: '11 Apr 2026, 08:00', status: 'Success' },
  { id: 2, database: 'hci_production', snapshot: 'Full snapshot', type: 'Auto',   size: '1.1 GB', createdAt: '10 Apr 2026, 08:00', status: 'Success' },
  { id: 3, database: 'hci_production', snapshot: 'Full snapshot', type: 'Manual', size: '1.1 GB', createdAt: '09 Apr 2026, 14:32', status: 'Success' },
  { id: 4, database: 'hci_production', snapshot: 'Full snapshot', type: 'Auto',   size: '0.9 GB', createdAt: '08 Apr 2026, 08:00', status: 'Failed'  },
  { id: 5, database: 'hci_branches',   snapshot: 'Partial snapshot', type: 'Auto', size: '312 MB', createdAt: '07 Apr 2026, 08:00', status: 'Partial' },
  { id: 6, database: 'hci_branches',   snapshot: 'Full snapshot', type: 'Manual', size: '290 MB', createdAt: '06 Apr 2026, 11:15', status: 'Success' },
  { id: 7, database: 'hci_production', snapshot: 'Full snapshot', type: 'Auto',   size: '1.0 GB', createdAt: '05 Apr 2026, 08:00', status: 'Success' },
];

const statusStyle = (status: BackupEntry['status']): React.CSSProperties => {
  switch (status) {
    case 'Success': return { background: '#e6f4ea', color: '#1e6e38', border: '1px solid #a8d5b5' };
    case 'Failed':  return { background: '#fdecea', color: '#a61c00', border: '1px solid #f5a9a9' };
    case 'Partial': return { background: '#fef9e7', color: '#7d5a00', border: '1px solid #f7d07a' };
  }
};

const BackupPanel: React.FC = () => {
  const toast                           = useRef<Toast>(null);
  const fileInputRef                    = useRef<HTMLInputElement>(null);
  const [runningBackup, setRunningBackup] = useState(false);
  const [restoringId,   setRestoringId]   = useState<number | null>(null);
  const [importingFile, setImportingFile] = useState(false);
  const [targetDb,      setTargetDb]      = useState('hci_db_beta');

  // ── Force backup now ──────────────────────────────────────────────────────
  const handleForceBackup = async () => {
    setRunningBackup(true);
    try {
      await apiClient.post('/node/restore/backup/run');
      toast.current?.show({ severity: 'success', summary: 'Backup triggered', detail: 'Backup completed successfully', life: 4000 });
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Backup failed', detail: err.response?.data?.error || 'Something went wrong', life: 5000 });
    } finally {
      setRunningBackup(false);
    }
  };

  // ── Restore from history row ──────────────────────────────────────────────
  const handleRestore = async (entry: BackupEntry) => {
    setRestoringId(entry.id);
    try {
      await apiClient.post('/node/restore', { targetDb });
      toast.current?.show({ severity: 'success', summary: 'Restore complete', detail: `Restored into ${targetDb}`, life: 4000 });
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Restore failed', detail: err.response?.data?.error || 'Something went wrong', life: 5000 });
    } finally {
      setRestoringId(null);
    }
  };

  // ── Import .gz file manually ──────────────────────────────────────────────
  const handleFileImport = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.gz')) {
      toast.current?.show({ severity: 'warn', summary: 'Invalid file', detail: 'Only .gz backup files are supported', life: 4000 });
      return;
    }

    if (!targetDb.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Target DB required', detail: 'Please enter a target database name', life: 4000 });
      return;
    }

    setImportingFile(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('targetDb', targetDb.trim());

    try {
      await apiClient.post('/v1/restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600_000, // 10 min — large restores take time
      });
      toast.current?.show({ severity: 'success', summary: 'Import complete', detail: `${file.name} restored into ${targetDb}`, life: 5000 });
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Import failed', detail: err.response?.data?.error || 'Something went wrong', life: 5000 });
    } finally {
      setImportingFile(false);
      // reset input so the same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [targetDb]);

  return (
    <div style={{ padding: '32px', fontFamily: 'inherit' }}>
      <Toast ref={toast} />

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1a1a1a' }}>Backup Panel</h2>
        <span style={{
          fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '999px',
          background: '#fff3cd', color: '#856404', border: '1px solid #ffc107', letterSpacing: '0.5px',
        }}>BETA</span>
      </div>
      <p style={{ margin: '0 0 28px 0', color: '#666', fontSize: '0.95rem' }}>
        Database backup history. Use restore to roll back to any successful snapshot.
      </p>

      {/* ── Action bar ── */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '12px',
        background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: '14px',
        padding: '20px 24px', marginBottom: '28px',
      }}>

        {/* Target DB input */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '220px' }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#555', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
            Target Database
          </label>
          <input
            type="text"
            value={targetDb}
            onChange={e => setTargetDb(e.target.value)}
            placeholder="e.g. hci_db_beta"
            style={{
              height: '40px', padding: '0 12px', border: '1px solid #d0d0d0',
              borderRadius: '8px', fontSize: '0.88rem', outline: 'none',
              background: '#fff', color: '#1a1a1a',
            }}
          />
        </div>

        {/* Import .gz file */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#555', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
            Import Backup File
          </label>
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            height: '40px', padding: '0 18px',
            background: importingFile ? '#f5f5f5' : '#fff',
            border: '1px solid #d0d0d0', borderRadius: '8px',
            fontSize: '0.85rem', fontWeight: 600, color: importingFile ? '#aaa' : '#1a1a1a',
            cursor: importingFile ? 'not-allowed' : 'pointer', transition: 'all 0.15s ease',
          }}>
            {importingFile
              ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: '0.9rem' }} /> Importing…</>
              : <><i className="pi pi-upload" style={{ fontSize: '0.9rem' }} /> Upload .gz</>
            }
            <input
              ref={fileInputRef}
              type="file"
              accept=".gz"
              disabled={importingFile}
              onChange={handleFileImport}
              style={{ display: 'none' }}
            />
          </label>
        </div>

        {/* Force backup now */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#555', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
            Force Backup
          </label>
          <button
            disabled={runningBackup}
            onClick={handleForceBackup}
            style={{
              height: '40px', padding: '0 18px', display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: runningBackup ? '#f5f5f5' : '#0f3d3e',
              border: '1px solid transparent', borderRadius: '8px',
              fontSize: '0.85rem', fontWeight: 600,
              color: runningBackup ? '#aaa' : '#fff',
              cursor: runningBackup ? 'not-allowed' : 'pointer', transition: 'all 0.15s ease',
            }}
          >
            {runningBackup
              ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: '0.9rem' }} /> Running…</>
              : <><i className="pi pi-play" style={{ fontSize: '0.9rem' }} /> Run Now</>
            }
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '28px' }}>
        {[
          { label: 'Total Backups', value: BACKUP_HISTORY.length },
          { label: 'Successful',    value: BACKUP_HISTORY.filter(b => b.status === 'Success').length },
          { label: 'Failed',        value: BACKUP_HISTORY.filter(b => b.status === 'Failed').length },
        ].map(stat => (
          <div key={stat.label} style={{ background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: '12px', padding: '20px 24px' }}>
            <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '6px', fontWeight: 500 }}>{stat.label}</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#1a1a1a' }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* ── Table ── */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '16px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              {['#', 'Database', 'Type', 'Size', 'Created At', 'Status', 'Action'].map(h => (
                <th key={h} style={{
                  padding: '14px 18px', textAlign: 'left', fontSize: '0.78rem',
                  fontWeight: 600, color: '#555', letterSpacing: '0.4px',
                  borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {BACKUP_HISTORY.map((entry, index) => (
              <tr
                key={entry.id}
                style={{ borderBottom: index < BACKUP_HISTORY.length - 1 ? '1px solid #f0f0f0' : 'none' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <td style={{ padding: '16px 18px', fontSize: '0.85rem', color: '#aaa' }}>
                  {String(entry.id).padStart(2, '0')}
                </td>
                <td style={{ padding: '16px 18px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1a1a1a' }}>{entry.database}</div>
                  <div style={{ fontSize: '0.78rem', color: '#999', marginTop: '2px' }}>{entry.snapshot}</div>
                </td>
                <td style={{ padding: '16px 18px' }}>
                  <span style={{
                    fontSize: '0.78rem', padding: '3px 10px', borderRadius: '6px',
                    background: entry.type === 'Manual' ? '#f0eaff' : '#eef2ff',
                    color: entry.type === 'Manual' ? '#5b21b6' : '#3730a3', fontWeight: 500,
                  }}>{entry.type}</span>
                </td>
                <td style={{ padding: '16px 18px', fontSize: '0.88rem', color: '#555' }}>{entry.size}</td>
                <td style={{ padding: '16px 18px', fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>{entry.createdAt}</td>
                <td style={{ padding: '16px 18px' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, padding: '4px 10px', borderRadius: '999px', ...statusStyle(entry.status) }}>
                    {entry.status}
                  </span>
                </td>
                <td style={{ padding: '16px 18px' }}>
                  <button
                    disabled={entry.status === 'Failed' || restoringId === entry.id}
                    onClick={() => handleRestore(entry)}
                    style={{
                      padding: '8px 18px', fontSize: '0.83rem', fontWeight: 600,
                      border: '1px solid #d0d0d0', borderRadius: '8px',
                      cursor: (entry.status === 'Failed' || restoringId === entry.id) ? 'not-allowed' : 'pointer',
                      background: (entry.status === 'Failed' || restoringId === entry.id) ? '#f5f5f5' : '#fff',
                      color: (entry.status === 'Failed' || restoringId === entry.id) ? '#bbb' : '#1a1a1a',
                      transition: 'all 0.15s ease', display: 'inline-flex', alignItems: 'center', gap: '6px',
                    }}
                    onMouseEnter={e => {
                      if (entry.status !== 'Failed' && restoringId !== entry.id) {
                        e.currentTarget.style.background = '#0f3d3e';
                        e.currentTarget.style.color = '#fff';
                        e.currentTarget.style.borderColor = '#0f3d3e';
                      }
                    }}
                    onMouseLeave={e => {
                      if (entry.status !== 'Failed' && restoringId !== entry.id) {
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.color = '#1a1a1a';
                        e.currentTarget.style.borderColor = '#d0d0d0';
                      }
                    }}
                  >
                    {restoringId === entry.id
                      ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: '0.8rem' }} /> Restoring…</>
                      : 'Restore'
                    }
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BackupPanel;
//hdfhdf